import { useMemo } from 'react';
import FlexPane from 'presentation/components/flex-pane/index.js';
import Text from 'presentation/components/text/index.js';
import ParticipantItem from 'presentation/components/participant-item/index.js';
import { sortParticipants } from 'presentation/components/scoreboard/utils.js';
import { getSeparatorTranslationKey } from 'presentation/formatters/participant-separator/index.js';
import { useCompetitorsIconsQuery } from 'presentation/components/competitor-icon/hooks/index.js';

export default function Participants(props) {
    const {
        event,
        mojitoTools: { config, stringResolver },
        drawLabel,
        forceDisplayEventName,
    } = props;

    const hasMoreThanOneParticipant = event.participants?.length > 1;
    const hasThreeParticipants = event?.participants?.length === 3;
    const showEventName = forceDisplayEventName || !hasMoreThanOneParticipant;

    const participants = useMemo(
        () => sortParticipants(event.participants, event.homeAway),
        [event.homeAway, event.participants]
    );

    const { icons } = useCompetitorsIconsQuery({
        config: config.competitorIconOptions,
        competitors: participants,
        sportId: event.sportId,
    });

    const hasTeamLogo = config.participantItem.showTeamLogo && icons.length > 0;

    return (
        <FlexPane config={config.container} class="ta-Participants">
            {showEventName ? (
                <EventNameLabel event={event} config={config} />
            ) : (
                <>
                    <ParticipantItem
                        config={config.participantItem}
                        sportId={event.sportId}
                        participant={participants[0]}
                        hasTeamLogo={hasTeamLogo}
                        src={icons[0]}
                    />
                    {config.showSeparator && (
                        <SeparatorLabel
                            hasTeamLogo={hasTeamLogo}
                            config={config}
                            text={stringResolver.resolveString(
                                getSeparatorTranslationKey(event.homeAway)
                            )}
                        />
                    )}
                    <ParticipantItem
                        config={config.participantItem}
                        sportId={event.sportId}
                        participant={participants[1]}
                        hasTeamLogo={hasTeamLogo}
                        src={icons[1]}
                    />
                    {hasThreeParticipants && config.showSeparator && (
                        <SeparatorLabel
                            hasTeamLogo={hasTeamLogo}
                            config={config}
                            text={stringResolver.resolveString(
                                getSeparatorTranslationKey(event.homeAway)
                            )}
                        />
                    )}
                    {hasThreeParticipants && (
                        <ParticipantItem
                            config={config.participantItem}
                            sportId={event.sportId}
                            participant={participants[2]}
                            hasTeamLogo={hasTeamLogo}
                            src={icons[2]}
                        />
                    )}
                    <DrawLabel config={config} text={drawLabel} />
                </>
            )}
        </FlexPane>
    );
}

const EventNameLabel = ({ event, config }) => {
    return (
        <Text config={config.outrightText} class="ta-outRightType">
            {event.name}
        </Text>
    );
};

const DrawLabel = ({ config, text }) => {
    if (!text) {
        return null;
    }

    return (
        <FlexPane config={config.drawLabelTextWrapper}>
            <Text class="ta-drawLabel" config={config.drawLabelText}>
                {text}
            </Text>
        </FlexPane>
    );
};

const SeparatorLabel = ({ config, text, hasTeamLogo }) => {
    if (!text) {
        return null;
    }

    const separatorWrapperConfig = hasTeamLogo
        ? config.separatorWrapperWithTeamLogo
        : config.separatorWrapper;

    return (
        <FlexPane config={separatorWrapperConfig}>
            <Text class="ta-separatorLabel" config={config.separatorText}>
                {text}
            </Text>
        </FlexPane>
    );
};
